/**
 * Behaviour for the content theme blocks.
 *
 * Deliberately separate from component-cro.js. That file owns the five
 * conversion elements that are already live on storefronts, and none of them
 * needed to change for any of this — so none of them were touched.
 *
 * Everything here is progressive enhancement. Tabs start as stacked panels,
 * the slider starts as a scrollable strip, the Lottie stage starts as its
 * poster. If this file fails to load, every block still shows its content.
 */
(function () {
  'use strict';

  /**
   * The element that actually sits inside `container`.
   *
   * Shopify wraps every theme block it renders through `content_for 'blocks'`
   * in a `<div class="shopify-block">`, so a nested block is a grandchild of
   * the element that declared it. Layout and visibility have to be applied to
   * that wrapper — hiding the inner panel would leave an empty wrapper taking
   * up space, and scrolling to the inner slide would use the wrong offset.
   *
   * Walking up instead of assuming the wrapper keeps this correct if Shopify
   * ever stops adding it.
   */
  function slotOf(node, container) {
    let current = node;
    while (current && current.parentElement && current.parentElement !== container) {
      current = current.parentElement;
    }
    return current || node;
  }

  /* --------------------------------------------------------------------------
     Content tabs

     The buttons are built from the panels that rendered, because a theme block
     cannot read its children's settings in Liquid. Each panel publishes its
     own label on data-tab-label and the strip is assembled from those.
     -------------------------------------------------------------------------- */

  if (!customElements.get('block-tabs')) {
    class BlockTabs extends HTMLElement {
      connectedCallback() {
        this.container = this.querySelector('[data-tabs-panels]');
        this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
        if (!this.container || this.panels.length < 2) return;

        this.slots = this.panels.map((panel) => slotOf(panel, this.container));
        this.buildTabs();
        this.select(0);
      }

      buildTabs() {
        const container = this.container;
        const list = document.createElement('div');
        list.className = 'tb-tabs__list';
        list.setAttribute('role', 'tablist');

        this.buttons = this.panels.map((panel, index) => {
          const id = panel.id || `tb-tab-panel-${Math.random().toString(36).slice(2, 9)}`;
          panel.id = id;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('tabindex', '0');

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'tb-tabs__tab';
          button.textContent = panel.dataset.tabLabel || `${index + 1}`;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', id);
          button.id = `${id}-tab`;
          panel.setAttribute('aria-labelledby', button.id);

          button.addEventListener('click', () => this.select(index));
          button.addEventListener('keydown', (event) => this.onKeydown(event, index));

          list.appendChild(button);
          return button;
        });

        this.insertBefore(list, container);
        this.classList.add('tb-tabs--ready');
      }

      /** Arrow keys move between tabs, which is what the tablist role promises. */
      onKeydown(event, index) {
        const last = this.buttons.length - 1;
        let next = null;

        if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        this.select(next);
        this.buttons[next].focus();
      }

      select(index) {
        this.panels.forEach((panel, i) => {
          const active = i === index;
          // Hide the slot, not the panel: an empty wrapper still takes space.
          this.slots[i].hidden = !active;
          panel.hidden = !active;
          this.buttons[i].setAttribute('aria-selected', active ? 'true' : 'false');
          this.buttons[i].tabIndex = active ? 0 : -1;
          this.buttons[i].classList.toggle('is-active', active);
        });
      }
    }

    customElements.define('block-tabs', BlockTabs);
  }

  /* --------------------------------------------------------------------------
     Slider

     The track is a real scroll container; the controls only call scrollTo. That
     keeps native touch momentum and means an overflowing slide can always be
     reached, even if a control is hidden or a script errors after this point.
     -------------------------------------------------------------------------- */

  if (!customElements.get('block-slider')) {
    class BlockSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-slider-track]');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        if (!this.track || this.slides.length < 2) return;

        // The scrollable children are the track's own children, which is what
        // scroll offsets and the intersection observer have to work with.
        this.slots = this.slides.map((slide) => slotOf(slide, this.track));

        this.prev = this.querySelector('[data-slider-prev]');
        this.next = this.querySelector('[data-slider-next]');
        this.dotsHost = this.querySelector('[data-slider-dots]');
        this.index = 0;
        this.loop = this.dataset.loop === 'true';

        this.buildControls();
        this.watchPosition();
        this.maybeAutoplay();
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        if (this.observer) this.observer.disconnect();
      }

      buildControls() {
        if (this.prev) {
          this.prev.hidden = false;
          this.prev.addEventListener('click', () => this.go(this.index - 1));
        }
        if (this.next) {
          this.next.hidden = false;
          this.next.addEventListener('click', () => this.go(this.index + 1));
        }

        if (!this.dotsHost) return;
        this.dotsHost.hidden = false;
        this.dots = this.slides.map((slide, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'tb-slider__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', `${i + 1}`);
          dot.addEventListener('click', () => this.go(i));
          this.dotsHost.appendChild(dot);
          return dot;
        });
      }

      /**
       * The current slide is read from what is actually on screen rather than
       * tracked in a counter, so a swipe, a keyboard scroll and a control press
       * all agree about where the slider is.
       */
      watchPosition() {
        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = this.slots.indexOf(entry.target);
              if (index !== -1) this.setActive(index);
            });
          },
          { root: this.track, threshold: 0.6 },
        );

        this.slots.forEach((slot) => this.observer.observe(slot));
      }

      setActive(index) {
        this.index = index;
        if (!this.dots) return;
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
      }

      go(target) {
        const last = this.slots.length - 1;
        let index = target;

        if (index < 0) index = this.loop ? last : 0;
        if (index > last) index = this.loop ? 0 : last;

        const slot = this.slots[index];
        if (!slot) return;

        // offsetLeft is measured against the nearest positioned ancestor, not
        // the track, so the difference of the two is the scroll position.
        this.track.scrollTo({ left: slot.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
        this.setActive(index);
      }

      maybeAutoplay() {
        if (this.dataset.autoplay !== 'true') return;

        // Autoplay is motion the visitor did not ask for, so it is skipped
        // entirely for anyone who has asked the system to reduce motion.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const seconds = parseInt(this.dataset.interval, 10) || 5;
        const start = () => {
          clearInterval(this.timer);
          this.timer = setInterval(() => this.go(this.index + 1), seconds * 1000);
        };
        const stop = () => clearInterval(this.timer);

        start();
        this.addEventListener('mouseenter', stop);
        this.addEventListener('mouseleave', start);
        this.addEventListener('focusin', stop);
        this.addEventListener('focusout', start);
      }
    }

    customElements.define('block-slider', BlockSlider);
  }

  /* --------------------------------------------------------------------------
     Countdown

     fixed / daily behave exactly like the countdown section.

     evergreen is per visitor. The deadline is written to storage on first view
     so a returning customer sees the clock where they left it instead of a
     fresh timer every visit, and "keep" — the default — leaves it expired once
     it has run out. Storage is wrapped in try/catch because a private window
     or blocked site data makes it throw, and a countdown is never worth an
     exception that stops the rest of the page.
     -------------------------------------------------------------------------- */

  if (!customElements.get('block-countdown')) {
    const SECOND = 1000;
    const MINUTE = 60 * SECOND;
    const HOUR = 60 * MINUTE;
    const DAY = 24 * HOUR;

    class BlockCountdown extends HTMLElement {
      connectedCallback() {
        this.display = this.querySelector('[data-countdown-display]');
        this.endedMessage = this.querySelector('[data-countdown-ended]');
        this.values = {
          days: this.querySelector('[data-countdown-value="days"]'),
          hours: this.querySelector('[data-countdown-value="hours"]'),
          minutes: this.querySelector('[data-countdown-value="minutes"]'),
          seconds: this.querySelector('[data-countdown-value="seconds"]'),
        };
        if (!this.display) return;

        this.target = this.resolveTarget();
        if (!this.target) {
          this.display.hidden = true;
          return;
        }

        this.tick();
        this.timer = setInterval(() => this.tick(), SECOND);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      store(mode) {
        try {
          return mode === 'session' ? window.sessionStorage : window.localStorage;
        } catch (error) {
          return null;
        }
      }

      read(key, mode) {
        const store = this.store(mode);
        if (!store) return null;
        try {
          return store.getItem(key);
        } catch (error) {
          return null;
        }
      }

      write(key, value, mode) {
        const store = this.store(mode);
        if (!store) return;
        try {
          store.setItem(key, value);
        } catch (error) {
          /* Storage full or blocked: the timer still runs for this page view. */
        }
      }

      resolveTarget() {
        if (this.dataset.mode === 'daily') return this.nextDailyCutoff();
        if (this.dataset.mode === 'evergreen') return this.evergreenTarget();

        const raw = this.dataset.target;
        if (!raw) return null;
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? null : date;
      }

      nextDailyCutoff() {
        const [hours, minutes] = (this.dataset.dailyTime || '').split(':').map(Number);
        if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

        const next = new Date();
        next.setHours(hours, minutes, 0, 0);
        if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1);
        return next;
      }

      evergreenTarget() {
        const minutes = parseInt(this.dataset.minutes, 10);
        if (!Number.isFinite(minutes) || minutes <= 0) return null;

        const mode = this.dataset.reset;
        const key = this.dataset.storageKey;
        const saved = parseInt(this.read(key, mode), 10);
        const now = Date.now();

        if (Number.isFinite(saved)) {
          if (saved > now) return new Date(saved);
          // Already expired for this visitor.
          if (mode !== 'restart') return new Date(saved);
        }

        const deadline = now + minutes * MINUTE;
        this.write(key, String(deadline), mode);
        return new Date(deadline);
      }

      tick() {
        const remaining = this.target.getTime() - Date.now();

        if (remaining <= 0) {
          clearInterval(this.timer);
          this.onEnd();
          return;
        }

        const parts = {
          days: Math.floor(remaining / DAY),
          hours: Math.floor((remaining % DAY) / HOUR),
          minutes: Math.floor((remaining % HOUR) / MINUTE),
          seconds: Math.floor((remaining % MINUTE) / SECOND),
        };

        // With the days unit hidden its hours must absorb them, or the clock
        // silently under-reports the time left.
        if (!this.values.days) parts.hours += parts.days * 24;

        Object.keys(parts).forEach((unit) => {
          const node = this.values[unit];
          if (!node) return;
          const text = unit === 'days' ? String(parts[unit]) : String(parts[unit]).padStart(2, '0');
          if (node.textContent !== text) node.textContent = text;
        });
      }

      onEnd() {
        if (this.dataset.onEnd === 'message' && this.endedMessage) {
          this.display.hidden = true;
          this.endedMessage.hidden = false;
          return;
        }
        this.hidden = true;
      }
    }

    customElements.define('block-countdown', BlockCountdown);
  }

  /* --------------------------------------------------------------------------
     Lottie

     The player is fetched only when a Lottie stage first scrolls into view, so
     a storefront that never uses the block pays nothing for it. Until then the
     poster image is what the visitor sees, and it stays if the fetch fails.
     -------------------------------------------------------------------------- */

  const LOTTIE_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie_light.min.js';
  let lottiePromise = null;

  function loadLottie() {
    if (lottiePromise) return lottiePromise;

    lottiePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = LOTTIE_SRC;
      script.async = true;
      script.onload = () => resolve(window.lottie);
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return lottiePromise;
  }

  function playLottie(stage) {
    loadLottie()
      .then((lottie) => {
        if (!lottie) return;
        const animation = lottie.loadAnimation({
          container: stage,
          renderer: 'svg',
          loop: stage.dataset.loop === 'true',
          autoplay: stage.dataset.autoplay === 'true',
          path: stage.dataset.src,
        });
        const speed = parseFloat(stage.dataset.speed);
        if (Number.isFinite(speed)) animation.setSpeed(speed);
        stage.classList.add('tb-lottie__stage--ready');
      })
      .catch(() => {
        /* Poster stays. Nothing else to do and nothing worth logging. */
      });
  }

  function initLottie() {
    const stages = document.querySelectorAll('[data-lottie]:not([data-lottie-init])');
    if (!stages.length) return;

    stages.forEach((stage) => {
      stage.setAttribute('data-lottie-init', '');
      if (!stage.dataset.src) return;

      if (!('IntersectionObserver' in window)) {
        playLottie(stage);
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.disconnect();
            playLottie(entry.target);
          });
        },
        { rootMargin: '200px' },
      );

      observer.observe(stage);
    });
  }

  /* --------------------------------------------------------------------------
     Clickable discount

     The link already applies the code through Shopify's own /discount route.
     Copying is the extra: the customer can paste it at checkout instead, and
     the label says so for a moment before returning to normal.
     -------------------------------------------------------------------------- */

  function onDiscountClick(event) {
    const link = event.target.closest('[data-discount-code]');
    if (!link || !navigator.clipboard) return;

    const label = link.querySelector('[data-discount-label]');
    const code = link.dataset.discountCode;
    if (!label || !code) return;

    navigator.clipboard.writeText(code).then(
      () => {
        const original = label.textContent;
        label.textContent = link.dataset.copiedLabel || original;
        setTimeout(() => {
          label.textContent = original;
        }, 1600);
      },
      () => {
        /* Clipboard refused. The link still applies the code, which is the
           part that matters. */
      },
    );
  }

  function init() {
    initLottie();
  }

  document.addEventListener('click', onDiscountClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The Theme Editor swaps section markup in place, so newly added blocks need
  // the one-off wiring (Lottie) run again. Custom elements re-register
  // themselves through connectedCallback and need nothing here.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
